<script setup lang="ts">
import { UIButton, UIChipButton, UIIcon } from '@groover-dev/groover-ui'
import { useFocus } from '@vueuse/core'
import throttle from 'lodash.throttle'
import { storeToRefs } from 'pinia'

import { vRemoveBodyScroll } from '~/directives/RemoveBodyScroll'

import ChartsPlayer from '~/components/dato/Charts/ChartsPlayer.vue'
import ChartsTrackList from '~/components/dato/Charts/ChartsTrackList.vue'
import DatoSectionContainer from '~/components/dato/DatoSectionContainer.vue'

import {
  usePageChartsTags,
  useSrollTrackIntoView,
} from '~/composables/usePageCharts'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useMiscResizeStore } from '~/stores/miscResize'

import { isDefined, isNonNanNumber } from '~/utils/type-guards'

import { provideFetchChartTracks } from '~/api-core/Charts/FetchCharts'

import { Breakpoints } from '~/enums/breakpoints'

import type { ChartsFragment } from '~/graphql/generated'
import type { ChartsData } from '~/types/ChartsPage'
import type { ChartsTrack } from '~/types/ChartsTrack'

type Props = ChartsFragment

withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  cta: null,
})

const {
  query: { shareTrackId, trackId: trackIdQueryParam },
  params: { slug: serializedTags },
} = useRoute()
const { DEV } = useRuntimeConfig()
const { coreFetch } = useProvideCoreFetch()
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { isAndroid, isIos } = useDevice()

// set up for focusing the mobile player
const mobileChartsPlayer = ref<HTMLElement | null>(null)
const { focused: isMobileChartsPlayerFocused } = useFocus(mobileChartsPlayer)

const shouldModalOpen = ref(false)
const selectedTrack = ref<ChartsTrack | undefined>()
const selectedChartsTrackToShare = ref<undefined | ChartsTrack>()

const ctaSize = computed(() => {
  if (SCREEN_WIDTH.value <= Breakpoints.sm) return 'small'
  if (SCREEN_WIDTH.value < Breakpoints.lg) return 'medium'
  return 'large'
})

const isModalDisplayed = computed(() => {
  return (
    SCREEN_WIDTH.value < Breakpoints.md &&
    selectedTrack.value &&
    shouldModalOpen.value
  )
})

const {
  selectedCountryName,
  selectedGenreName,
  sortedAvailableGenres,
  tagsDataForFetch,
} = await usePageChartsTags()
const { data: chartsData, error } = useAsyncData<ChartsData>(
  'chartsData',
  async () => {
    const results = await fetchChartsTrackResults()

    const tracks: ChartsData = {
      ...results,
      foundTrack: undefined as ChartsTrack | undefined,
    }
    if (!tracks.results.length) return tracks

    const trackId = Number(trackIdQueryParam) || 0
    if (!trackId) return tracks

    // when trackId query param is present, we need to fetch pages of results until we find it
    const foundTrackInFirstResultsSet = tracks.results.find(
      (track) => track.track_id === trackId,
    )
    if (foundTrackInFirstResultsSet)
      return { ...tracks, foundTrack: foundTrackInFirstResultsSet }

    if (!tracks.next) return tracks

    const foundTracks = await fetchChartTracksUntilTrackIsFound(
      trackId,
      getCursorFromUrl(tracks.next || ''),
      tracks.results,
    )

    if (foundTracks.results.length) {
      return {
        ...foundTracks,
        results: foundTracks.results,
        next: foundTracks.next,
      } satisfies ChartsData
    }

    return tracks
  },
  {
    default: () => ({
      next: null,
      previous: null,
      results: [],
      foundTrack: undefined,
    }),
  },
)

// Failure to get the data will result in a 404 in prod
if (error.value) {
  if (DEV === 'true') throw createError(error.value)
  else throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

useSrollTrackIntoView(
  trackIdQueryParam,
  chartsData,
  handleTrackSelected,
  handleMobileChartsPlayerClose,
)

// set first track to be selected by default
selectedTrack.value = chartsData.value?.results[0]

const hasFilterSelected = computed(() => {
  return (
    selectedCountryName.value !== 'all' || selectedGenreName.value !== 'all'
  )
})

onMounted(() => {
  if (!isAndroid && !isIos) return

  const parsedNumber = Number(shareTrackId)

  if (!isNonNanNumber(parsedNumber)) return

  const chartsTrack = chartsData.value?.results.find(
    ({ track_id: trackId }) => trackId === parsedNumber,
  )

  if (!chartsTrack) return
  selectedChartsTrackToShare.value = chartsTrack
})

const throttledHandleTrackSelected = throttle(handleTrackSelected, 350, {
  trailing: false,
})

async function fetchChartTracksUntilTrackIsFound(
  trackId: number,
  cursor: Record<string, string>,
  prevResults: ChartsTrack[],
): Promise<ChartsData> {
  const tracks = await fetchChartsTrackResults(cursor)

  // there are no more results to look through
  if (!tracks.results.length) return tracks

  // add previous results to the current results
  tracks.results = [...prevResults, ...tracks.results]

  const foundTrack = tracks.results.find((track) => track.track_id === trackId)
  // if we found the track or there aren't anymore pages to search for return
  if (foundTrack || !tracks.next) return { ...tracks, foundTrack }

  // if we haven't found the track yet, keep looking
  const nextCursor = getCursorFromUrl(tracks.next || '')
  return fetchChartTracksUntilTrackIsFound(trackId, nextCursor, tracks.results)
}

async function fetchChartsTrackResults(cursor?: Record<string, string>) {
  const fetchChartTracks = provideFetchChartTracks(coreFetch)
  const { country, genre } = tagsDataForFetch.value

  return await fetchChartTracks(cursor, country, genre).catch(() => ({
    next: null,
    previous: null,
    results: [],
  }))
}

function getCursorFromUrl(url: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(url.split('?')[1]))
}

function handleTrackSelected(payload: { trackId: number }) {
  selectedTrack.value = chartsData.value?.results.find(
    (track) => track.track_id === payload.trackId,
  )
  shouldModalOpen.value = true
}

function handleMobileChartsPlayerClose() {
  shouldModalOpen.value = false
}

async function handleLoadMoreClick() {
  if (!chartsData.value?.next) return

  const cursor = getCursorFromUrl(chartsData.value.next)
  if (!cursor) return

  const results = await fetchChartsTrackResults(cursor)
  chartsData.value = {
    ...chartsData.value,
    results: [...(chartsData.value?.results || []), ...results.results],
    next: results.next,
  }
}
</script>

<template>
  <DatoSectionContainer class="tw-relative tw-flex-col">
    <LazyChartsShareOnInstagramModal
      v-if="selectedChartsTrackToShare"
      :charts-track="selectedChartsTrackToShare"
      :display="isDefined(selectedChartsTrackToShare)"
      :selected-country="selectedCountryName"
      :selected-genre="selectedGenreName"
      @update:display="selectedChartsTrackToShare = undefined"
    />
    <div class="tw-flex tw-flex-col tw-gap-lg md:tw-gap-y-2xl lg:tw-flex-row">
      <div
        v-if="title || subtitle"
        class="tw-flex tw-w-full tw-flex-col tw-gap-y-lg md:tw-gap-y-sm"
      >
        <h2 v-if="title" class="tw-text-h3">
          {{ `${title} - ${$t(`charts.tags.country.${selectedCountryName}`)}` }}
        </h2>
        <p v-if="subtitle" class="tw-text-body-sm md:tw-text-body">
          {{ subtitle }}
        </p>
      </div>
      <UIButton
        v-if="cta?.url && !hasFilterSelected"
        :to="cta.url"
        :text="cta.text"
        target="_blank"
        class="tw-bg-orange-500 md:tw-place-self-start lg:tw-place-self-end"
        :size="ctaSize"
      />
    </div>
    <hr
      v-if="title || subtitle || cta"
      class="tw-border-discrete-3 tw-m-auto tw-mt-2xl tw-hidden tw-w-full md:tw-block"
    />
    <div
      v-if="sortedAvailableGenres.length"
      class="tw-mt-lg tw-flex tw-flex-wrap tw-gap-sm"
    >
      <UIChipButton
        key="all"
        :color="selectedGenreName === 'all' ? 'alt' : undefined"
        :text="$t('charts.tags.genre.all')"
        to="/charts/"
      />
      <UIChipButton
        v-for="genre in sortedAvailableGenres"
        :key="genre"
        :color="genre === serializedTags[1] ? 'alt' : undefined"
        :text="$t(`charts.tags.genre.${genre}`)"
        :to="
          selectedGenreName === genre
            ? `/charts/${selectedCountryName}/`
            : `/charts/${selectedCountryName}/${genre}/`
        "
      />
    </div>
    <div
      v-if="chartsData?.results.length"
      class="md:tw-grid md:tw-grid-cols-[1fr_40.5%] md:tw-items-start md:tw-gap-x-lg"
      :class="{
        'tw-mt-2xl md:tw-mt-4xl lg:tw-mt-6xl': title || subtitle || cta,
      }"
    >
      <ChartsTrackList
        :track-list="chartsData.results"
        :num-tracks-preloaded="chartsData.results.length"
        @track-selected="throttledHandleTrackSelected"
        @share-charts-track="selectedChartsTrackToShare = $event"
      />
      <!-- Mobile Load more button -->
      <UIButton
        v-if="chartsData?.next"
        :text="$t('common.loadMore')"
        class="tw-mt-2xl tw-w-full tw-bg-orange-500 md:tw-hidden"
        @click="handleLoadMoreClick"
      />
      <!-- Mobile "listen now" button -->
      <button
        type="button"
        class="tw-sticky tw-bottom-lg tw-left-0 tw-z-50 tw-mt-xl tw-w-full tw-rounded-lg tw-bg-fill-alt tw-p-sm tw-pt-2xs tw-shadow-xl md:tw-hidden"
        @click="shouldModalOpen = true"
      >
        <span class="tw-flex tw-flex-col tw-items-center">
          <UIIcon
            name="mdi:chevron-up"
            size="sm"
            class="tw-text-icon-discrete3"
            aria-hidden
          />
          <span class="tw-text-body-sm tw-text-inverse">{{
            $t('common.listenNow')
          }}</span>
        </span>
      </button>
      <!-- Mobile player -->
      <ClientOnly>
        <Teleport to="#modal-backdrop-overlay">
          <Transition
            name="fade"
            @after-enter="isMobileChartsPlayerFocused = true"
          >
            <!-- use of v-show & v-if on the same element is intentional -->
            <!-- v-show allows playback to continue on mobile when a user closes the modal -->
            <div
              v-if="SCREEN_WIDTH < Breakpoints.md"
              v-show="isModalDisplayed"
              v-remove-body-scroll="isModalDisplayed"
              class="tw-fixed tw-left-0 tw-top-0 tw-z-50 tw-flex tw-min-h-dvh tw-min-w-full tw-items-end tw-bg-[#000000] tw-bg-opacity-50 tw-p-lg md:tw-hidden"
              role="dialog"
              aria-labelledby="trackName"
              @click.stop="shouldModalOpen = false"
            >
              <!-- @click.stop prevents player clicks from auto-closing the modal -->
              <ChartsPlayer
                v-if="selectedTrack"
                ref="mobileChartsPlayer"
                :track-url="selectedTrack.track_link"
                :track-artist="selectedTrack.band_name"
                :track-name="selectedTrack.track_name"
                class="tw-w-full"
                tabindex="0"
                @click.stop
                @close="handleMobileChartsPlayerClose"
              />
            </div>
          </Transition>
        </Teleport>
        <!-- Desktop player -->
        <Transition name="fade">
          <ChartsPlayer
            v-if="SCREEN_WIDTH >= Breakpoints.md && selectedTrack"
            :track-url="selectedTrack.track_link"
            :track-artist="selectedTrack.band_name"
            :track-name="selectedTrack.track_name"
            class="tw-sticky tw-right-0 tw-top-[140px]"
          />
        </Transition>
      </ClientOnly>
    </div>
    <!-- Medium + Desktop Load more button -->
    <UIButton
      v-if="chartsData?.next"
      :text="$t('common.loadMore')"
      class="tw-mx-auto tw-mt-2xl tw-hidden tw-w-full tw-bg-orange-500 md:tw-mt-4xl md:tw-block md:tw-w-fit lg:tw-mt-6xl"
      @click="handleLoadMoreClick"
    />
  </DatoSectionContainer>
</template>
